import { Cart, CartTotalResponse } from '@diamond/shared/types';

import type { PriceCalculationParams } from '../price-calculation';
import {
  calculateDiscountPercentage,
  resolveDisplayPrice,
  resolvePromoDeductionAmount,
  resolvePromoIsDiscount,
} from '../price-calculation';
import { processPromos } from '../promos/promos';

export interface CartItemPromo {
  hasDiscount: boolean;
  priceAfterDiscount: number;
  hasBundledDiscount: boolean;
  discountPercentage: number;
  promoDeductionAmount: number;
  promoIsDiscount: boolean;
  displayPrice: number;
}

export const findActivePromo = (
  divisionPromos: CartTotalResponse['promos'][number],
  productId: string
) => {
  const { single_free_good_promos, bundled_free_good_promos } = divisionPromos;

  return [
    ...(single_free_good_promos?.filter(
      (promo) =>
        promo.ref_product_id === productId || promo.product_id === productId
    ) || []),
    ...(bundled_free_good_promos?.filter(
      (promo) => promo.product_id === productId
    ) || []),
  ];
};

export const processCartItemPromo = (
  item: Cart,
  cartTotalData?: CartTotalResponse,
  compiledPromo?: any
): CartItemPromo => {
  const productId = item.product.id;

  // Find division-level promos
  const divisionPromos = cartTotalData?.promos?.find(
    (p) => p.division === item.product.division
  );

  const activePromos = divisionPromos
    ? findActivePromo(divisionPromos, productId)
    : [];

  // Update product with active free goods promos
  const updatedProduct = activePromos?.length
    ? {
        ...item.product,
        free_good_promos: activePromos as typeof item.product.free_good_promos,
      }
    : item.product;

  const processedPromos = processPromos(updatedProduct);
  const { hasDiscount, priceAfterDiscount, hasBundledDiscount } =
    processedPromos;

  const compiledPromoPrice = compiledPromo?.price_after_discount || 0;
  const hasCompiledDiscount = compiledPromoPrice > 0;
  const unitPrice = item.product.prices[0].price;

  const priceParams: PriceCalculationParams = {
    hasCompiledDiscount,
    compiledPromoPrice,
    hasDiscount,
    isActive:
      hasCompiledDiscount ||
      (!hasCompiledDiscount &&
        !hasBundledDiscount &&
        item.product.minimum_order_quantity >=
          item.product.discount_promos[0]?.minimum_order_quantity),
    priceAfterDiscount,
    unitPrice,
  };

  const displayPrice = resolveDisplayPrice(priceParams);

  const promoResolutionParams: any = {
    hasCompiledDiscount,
    compiledPromo,
    hasBundledDiscount,
    product: item.product,
  };

  const hasAnyPromo =
    (hasCompiledDiscount && compiledPromo) || hasBundledDiscount || hasDiscount;

  const promoDeductionAmount = hasAnyPromo
    ? resolvePromoDeductionAmount(promoResolutionParams)
    : 0;

  const discountPercentage =
    priceParams.isActive && hasAnyPromo
      ? calculateDiscountPercentage(promoDeductionAmount)
      : 0;

  const promoIsDiscount = hasAnyPromo
    ? resolvePromoIsDiscount(promoResolutionParams)
    : false;

  return {
    hasDiscount,
    priceAfterDiscount,
    hasBundledDiscount,
    discountPercentage,
    promoDeductionAmount,
    promoIsDiscount,
    displayPrice,
  };
};
